import Cached from '@mui/icons-material/Cached'
import Check from '@mui/icons-material/Check'
import Info from '@mui/icons-material/Info'
import Download from '@mui/icons-material/Download'
import PrintOutlined from '@mui/icons-material/PrintOutlined'
import Save from '@mui/icons-material/Save'
import Settings from '@mui/icons-material/Settings'
import { Box, Button, Chip, Dialog, Drawer, Tooltip, Typography } from '@mui/material'
import axios from 'axios'
import { Spinner } from 'react-bootstrap'
import dayjs from 'dayjs'
import {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router'
import { setEmrSectionsData } from 'src/clinic/reducers/prescription/prescriptionReducer'
import generatePDF, { Margin } from 'react-to-pdf'
import { toast } from 'react-toastify'
import LSPastVisitReportsComponent from 'src/consumer/components/LiveSessionPage/components/LSPastVisitReportsComponent/LSPastVisitReportsComponent'
import { LoadingSpinner } from 'src/consumer/components/Loaders'
import { GENDER_LIST_ONBOARDING } from 'src/consumer/constants'
import { getAgeFromDobWithTime } from 'src/consumer/helpers/profilePageHelper'
import { DocSideContext } from 'src/context/DocSideContextProvider'
import EmrSectionsEditorDialog from 'src/mentor/components/PrescriptionMakingPage/components/EmrSectionsEditorDialog'
import useEmrSectionsEditorDialog from 'src/mentor/components/PrescriptionMakingPage/screeningReportViewer/hooks/useEmrSectionsEditorDialog'
import PsychAssessmentViewerAndSignaller from 'src/mentor/components/AssessmentReport/components/PsychAssessmentViewerAndSignaller'
import AssessmentReportBody from 'src/mentor/components/AssessmentReport/viewComponents/AssessmentReportBody'
import AssessmentReportHeader from 'src/mentor/components/AssessmentReport/viewComponents/AssessmentReportHeader'
import { PatientNotesSectionForReports } from 'src/mentor/components/PrescriptionMakingPage/PrescriptionMakingPageComponents'
import {
  PMPButtonForConsultation,
  PMPParent,
  PMPSectionHeading,
  PMPSectionInputLabel,
  PrescriptionTabFinalParent,
} from 'src/mentor/components/PrescriptionMakingPage/PrescriptionMakingPageStyles'
import {
  getCountryCode,
  getMainLanguageCode,
  mentorApiURL,
} from 'src/mentor/services'
import TherapyReportConfirmation from 'src/mentor/components/TherapyReportMakingPage/components/TherapyReportConfirmation'
import TipTapEditor from 'src/mentor/components/TherapyReportMakingPage/components/TipTapEditor'
import {
  getDateOfTherapyReport
} from 'src/mentor/components/TherapyReportMakingPage/TherapyReportMakingPageComponents'
import {
  getDataMapFromCommonResponse
} from 'src/mentor/helpers/utilityFunctions'
import { getMPData, getMPDataWithoutParams, postMPDataWithoutParams } from 'src/mentor/services'
import { ASSESSMENT_REPORT_NARRATIVE_SECTIONS } from 'src/mentor/components/AssessmentReport/assessmentReportSectionConfig'
import PractitionerAssessmentDrawer from 'src/mentor/components/PractitionerAssessment/PractitionerAssessmentDrawer'
import { getEventAssessments, getAssessmentRuns } from 'src/mentor/services/practitionerAssessmentService'
import AssessmentReportRunDetails from 'src/mentor/components/AssessmentReport/viewComponents/AssessmentReportRunDetails'

const AssessmentReportMakingPage = forwardRef(
  (
    {
      propEventId,
      propPatientId,
      propClinicId,
      propClinicBrandId,
      propMentorProfileData,
      propBookedEventInfo,
      triggerSave,
      setTriggerSave,
      leaveRoom,
      afterSaveFunction,
      auxiliaryDataFromProps,
      getProcessAndSetEventData,
      chamberRoomId,
    },
    ref,
  ) => {
    // const notesQuillRef = useRef(null)
    const hasMounted = useRef(false)
    const navigate = useNavigate()
    const [mentorProfileData, setMentorProfileData] = useState()
    const [mentorProfileDataLoading, setMentorProfileDataLoading] = useState(false)
    const [therapyReportId, setTherapyReportId] = useState(null)
    const targetRef = useRef()
    const urlParams = new URLSearchParams(window.location.search)
    const eventId = propEventId || urlParams.get('eventId')
    const patientId = propPatientId || urlParams.get('patientId')
    const clinicId = propClinicId || urlParams.get('clinicId')
    const clinicBrandId = propClinicBrandId
    const [prescriptionSaveLoading, setPrescriptionSaveLoading] = useState(false)
    const bookedEventInfo = propBookedEventInfo ?? null

    const [therapyReportData, setTherapyReportData] = useState(null)
    const [observationAndAdvice, setObservationAndAdvice] = useState()
    const [notes, setNotes] = useState('')
    const [selectedComplaints, setSelectedComplaints] = useState()
    const [selectedDiagnosis, setSelectedDiagnosis] = useState()
    const [nextVisitInputValue, setNextVisitInputValue] = useState()
    const [nextVisitInputType, setNextVisitInputType] = useState('days')

    /** Snapshot of narrative section HTML for autosave dirty check */
    const getNarrativeSnapshot = () => ({
      clinicalInterpretation: assessmentReportDataToSave?.clinicalInterpretation ?? '',
      functionalImpact: assessmentReportDataToSave?.functionalImpact ?? '',
      clinicalObservations: assessmentReportDataToSave?.clinicalObservations ?? '',
      provisionalClinicalImpression: assessmentReportDataToSave?.provisionalClinicalImpression ?? '',
      recommendations: assessmentReportDataToSave?.recommendations ?? '',
      freeFormRichText: assessmentReportDataToSave?.freeFormRichText ?? '',
    })

    const [isPrintingPastHistoryReports, setIsPrintingPastHistoryReports] = useState(false)

    const [therapyReportTemplate, setTherapyReportTemplate] = useState()

    const [userEditing, setUserEditing] = useState(false)

    const lastSavedState = useRef({}) // Store last saved state

    const notesRef = useRef(null)
    const {
      open: emrSectionsEditorOpen,
      handleOpen: emrSectionsEditorHandleOpen,
      handleClose: emrSectionsEditorHandleClose,
    } = useEmrSectionsEditorDialog()
    /** Launcher action drawer: when user clicks Start/Resume/View Result etc. from the Assessment Test Launcher list */
    const [launcherActionDrawerOpen, setLauncherActionDrawerOpen] = useState(false)
    const [launcherActionAssessmentId, setLauncherActionAssessmentId] = useState(null)
    const [launcherActionClinicalAssessmentTitle, setLauncherActionClinicalAssessmentTitle] = useState(null)
    const [launcherAction, setLauncherAction] = useState(null)
    const [launcherActionCompletedRunId, setLauncherActionCompletedRunId] = useState(null)
    const [eventAssessmentsForDrawer, setEventAssessmentsForDrawer] = useState([]) // full eventAssessments from API for drawer to avoid duplicate call
    /** EMR sections for assessment-report (ordered, selected); drives layout like prescription page */
    const [assessmentReportEmrSections, setAssessmentReportEmrSections] = useState([]) // sections for editor (selected, enabled, !printOnly)
    const [assessmentReportEmrSectionsForPrint, setAssessmentReportEmrSectionsForPrint] = useState([]) // sections for print (selected, enabled, printable, !editorOnly)
    /** Completed assessment runs for report print/preview (when Assessment Test Launcher section is enabled) */
    const [assessmentRunsForPrint, setAssessmentRunsForPrint] = useState([])

    const [autoSaveStatus, setAutoSaveStatus] = useState({ text: '', status: 'idle' })
    const [showConfirmation, setShowConfirmation] = useState(false)
    const [downloadingPdf, setDownloadingPdf] = useState(false)

    const [nextVisitOmissionReason, setNextVisitOmissionReason] = useState('')

    const [assessmentReportDataToSave, setAssessmentReportDataToSave] = useState(null)

    const assessmentDrawerBookedEventInfo = useMemo(() => {
      const base = bookedEventInfo ?? propBookedEventInfo ?? null
      if (!base && !therapyReportData) return base

      const fallbackDateOfBirth =
        base?.patientInfoData?.dateOfBirth ??
        base?.patientInfo?.dateOfBirth ??
        base?.patient?.dateOfBirth ??
        base?.patientDateOfBirth ??
        therapyReportData?.patientDateOfBirth ??
        null

      const fallbackGender =
        base?.patientInfoData?.gender ??
        base?.patientInfo?.gender ??
        base?.patient?.gender ??
        base?.patientGender ??
        therapyReportData?.patientGender ??
        null

      const fallbackName =
        base?.patientInfoData?.name ??
        base?.patientInfo?.name ??
        base?.patient?.name ??
        base?.patientName ??
        therapyReportData?.patientName ??
        null

      const fallbackPatientNumber =
        base?.patientInfoData?.patientNumber ??
        base?.patientInfo?.patientNumber ??
        base?.patient?.patientNumber ??
        base?.patientNumber ??
        therapyReportData?.patientNumber ??
        null

      return {
        ...(base ?? {}),
        patientDateOfBirth: base?.patientDateOfBirth ?? fallbackDateOfBirth ?? null,
        patientGender: base?.patientGender ?? fallbackGender ?? null,
        patientName: base?.patientName ?? fallbackName ?? null,
        patientNumber: base?.patientNumber ?? fallbackPatientNumber ?? null,
        patientInfoData: {
          ...(base?.patientInfoData ?? {}),
          dateOfBirth: base?.patientInfoData?.dateOfBirth ?? fallbackDateOfBirth ?? null,
          gender: base?.patientInfoData?.gender ?? fallbackGender ?? null,
          name: base?.patientInfoData?.name ?? fallbackName ?? null,
          patientNumber:
            base?.patientInfoData?.patientNumber ?? fallbackPatientNumber ?? null,
        },
      }
    }, [bookedEventInfo, propBookedEventInfo, therapyReportData])

    const dispatch = useDispatch()
    const { setFlowId, setContextId, setButtonTopOffset } = useContext(DocSideContext)

    const therapyDate = propBookedEventInfo?.startTime

    /** Apply saved EMR section data so report updates immediately without refetch/flicker.
     * Editor: only sections that are selected and enabled (like prescription page).
     * Print: only sections that are selected, enabled, printable and not editor-only. */
    const applyAssessmentReportEmrSectionsFromMap = useCallback((dataMap) => {
      const reportData = dataMap?.['assessment-report']
      const rawSections = reportData?.emrSections ?? []
      const withOrder = rawSections
        .filter((s) => Boolean(s?.selected) && s?.enabled !== false)
        .sort((a, b) => Number(a?.order ?? 0) - Number(b?.order ?? 0))
      setAssessmentReportEmrSections(withOrder.filter((s) => s.printOnly !== true))
      setAssessmentReportEmrSectionsForPrint(
        withOrder.filter((s) => s.printable !== false && s.editorOnly !== true),
      )
    }, [])

    const refreshEmrSections = useCallback(
      (syncedData) => {
        if (syncedData && typeof syncedData === 'object' && Object.keys(syncedData).length > 0) {
          dispatch(setEmrSectionsData(syncedData))
          applyAssessmentReportEmrSectionsFromMap(syncedData)
          return
        }
        getProcessAndSetEventData?.({ hideLoader: true })
      },
      [dispatch, applyAssessmentReportEmrSectionsFromMap, getProcessAndSetEventData],
    )

    function setUserEditingState(setStateFunction, setStateVal) {
      console.log({ setStateFunction, setStateVal })
      console.log('setting user editing as true')
      setUserEditing(true)
      setStateFunction(setStateVal)
    }

    async function getAndSetMentorProfileData() {
      if (propMentorProfileData) {
        setMentorProfileData(propMentorProfileData)
        return propMentorProfileData
      } else {
        setMentorProfileDataLoading(true)
        const resp = await getMPData('/api/secure/teacher/profile')
        setMentorProfileDataLoading(false)
        if (resp?.status === 200) {
          setMentorProfileData(resp?.data)
        }
        return resp?.data
      }
    }

    async function processAndSetTherapyPageData(reportData) {
      const mainData = reportData || {}

      setTherapyReportData(mainData)
      setTherapyReportId(mainData?.id ?? mainData?.assessmentReportId ?? null)
      setNotes(mainData?.freeFormRichText || mainData?.content || '')

      setAssessmentReportDataToSave(mainData)
      lastSavedState.current = {
        ...lastSavedState.current,
        narrative: {
          clinicalInterpretation: mainData?.clinicalInterpretation ?? '',
          functionalImpact: mainData?.functionalImpact ?? '',
          clinicalObservations: mainData?.clinicalObservations ?? '',
          provisionalClinicalImpression: mainData?.provisionalClinicalImpression ?? '',
          recommendations: mainData?.recommendations ?? '',
          freeFormRichText: mainData?.freeFormRichText ?? '',
        },
      }
    }

    async function getAndSetTherapyReportData() {
      let therapyReportData
      let templateData

      if (auxiliaryDataFromProps) {
        therapyReportData = auxiliaryDataFromProps?.assessmentReport
        templateData = auxiliaryDataFromProps?.therapyReportTemplate
      }
      // const response = await getMPDataWithoutParams(
      //   `/api/secure/patient/therapy-report/create-by-eventId?eventId=${eventId}`,
      // )
      // if (response.status === 200 && isCommonResponseSuccessful(response.data.code)) {
      //   therapyReportData = response.data.data.assessmentReport
      //   templateData = response.data.data.therapyReportTemplate
      // } else {
      //   toast.error('Error getting therapy report data')
      // }
      console.log({ therapyReportData, auxiliaryDataFromProps })
      processAndSetTherapyPageData(therapyReportData)
      setTherapyReportTemplate(templateData)
    }

    function renderInfoText() {
      if (autoSaveStatus.status === 'idle') {
        return <></>
      }

      console.log({ prescriptionSaveLoading })

      if (prescriptionSaveLoading) {
        return (
          <Box
            sx={{
              color: 'unFocused.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}
          >
            <Cached fontSize="18px" />
            <span>Saving</span>
          </Box>
        )
      }

      return autoSaveStatus.status === 'error' ? (
        <Box
          sx={{
            color: 'error.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
          }}
        >
          <Info fontSize="18px" />
          <span>{autoSaveStatus.text}</span>
        </Box>
      ) : (
        <Box
          sx={{
            color: 'success.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
          }}
        >
          <Check fontSize="18px" />
          <span>{autoSaveStatus.text}</span>
        </Box>
      )
    }

    async function setup() {
      const response = await getAndSetMentorProfileData()
      if (eventId && patientId) {
        getAndSetTherapyReportData()
      } else {
        navigate('/404')
      }
    }

    useImperativeHandle(ref, () => ({
      checkAndOpenModal: () => {
        console.log({ nextVisitInputValue })
        if (!nextVisitInputValue) {
          setShowConfirmation(true)
        } else {
          leaveRoom(true) // Call the parent's main action if state is false
        }
      },
      saveTherapyReport: (hideToast, isRefreshingData) => saveTherapyReport(hideToast, isRefreshingData),
      prescriptionSaveLoading,
      openSettings: () => emrSectionsEditorHandleOpen(),
    }))

    async function saveReason() {
      await saveTherapyReport(true, false)
      leaveRoom(true)
    }

    useEffect(() => {
      console.info(therapyReportTemplate)
    }, [therapyReportTemplate])

    async function saveTherapyReport(hideToast = false, isRefreshingData = true) {
      setPrescriptionSaveLoading(true)
      if (notesRef.current?.saveNote) {
        notesRef.current.saveNote()
      }
      const report = assessmentReportDataToSave || {}
      const assessmentReportPayload = {
        bookedEventId: eventId,
        patientId: patientId,
        psychAssessmentId: propBookedEventInfo?.psychAssessmentIds?.[0] ?? report?.psychAssessmentId ?? null,
        id: therapyReportId ?? report?.id ?? null,
        assessmentReportId: therapyReportId ?? report?.assessmentReportId ?? report?.id ?? null,
        answers: report?.answers,
        summaryAnswers: report?.summaryAnswers,
        clinicalInterpretation: report?.clinicalInterpretation ?? '',
        functionalImpact: report?.functionalImpact ?? '',
        clinicalObservations: report?.clinicalObservations ?? '',
        provisionalClinicalImpression: report?.provisionalClinicalImpression ?? '',
        recommendations: report?.recommendations ?? '',
        freeFormRichText: report?.freeFormRichText ?? '',
        content: report?.content ?? '',
        observation: report?.observation ?? '',
        advice: report?.advice ?? '',
      }
      const body = { assessmentReport: assessmentReportPayload }
      const response = await postMPDataWithoutParams(
        '/api/secure/mentor/emr-reports/save?reportType=assessment-report',
        body,
      )
      setUserEditing(false)
      setPrescriptionSaveLoading(false)
      if (response?.status === 200 && (response?.data?.code === 2000 || response?.data?.code === 200)) {
        setAutoSaveStatus({ text: 'Report Saved', status: 'saved' })
        setTimeout(() => {
          setAutoSaveStatus({ text: '', status: 'idle' })
        }, 3000)
        const savedReport = getDataMapFromCommonResponse(response)?.assessmentReport ?? response?.data?.data?.assessmentReport ?? response?.data?.assessmentReport
        if (savedReport) {
          setTherapyReportData((prev) => ({ ...prev, ...savedReport }))
          setTherapyReportId(savedReport?.id ?? savedReport?.assessmentReportId ?? null)
          setAssessmentReportDataToSave((prev) => ({ ...prev, ...savedReport }))
          lastSavedState.current = {
            ...lastSavedState.current,
            narrative: {
              clinicalInterpretation: savedReport?.clinicalInterpretation ?? '',
              functionalImpact: savedReport?.functionalImpact ?? '',
              clinicalObservations: savedReport?.clinicalObservations ?? '',
              provisionalClinicalImpression: savedReport?.provisionalClinicalImpression ?? '',
              recommendations: savedReport?.recommendations ?? '',
              freeFormRichText: savedReport?.freeFormRichText ?? '',
            },
          }
        }
        if (isRefreshingData && getProcessAndSetEventData) {
          await getProcessAndSetEventData()
        }
        if (!hideToast) {
          toast.success('Saved Assessment Report')
        }
      } else {
        if (!hideToast) {
          toast.error(response?.data?.message || "Couldn't save Assessment report.")
        }
        setAutoSaveStatus({ text: 'Save Failed', status: 'error' })
      }
    }

    async function saveAndPrint() {
      await saveTherapyReport()
      window.print()
    }

    async function downloadAssessmentReportPdf() {
      if (!eventId) {
        toast.error('Unable to download: Missing event ID')
        return
      }
      try {
        setDownloadingPdf(true)
        const token = localStorage.getItem('token')
        const countryCode = getCountryCode()
        const mainLanguageCode = getMainLanguageCode()
        const response = await axios.get(`${mentorApiURL}/api/secure/mentor/emr-reports/download`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'ef-user-country-code': countryCode,
            'ef-app-language': mainLanguageCode,
          },
          params: {
            reportType: 'assessment-report',
            eventId,
            languageCode: mainLanguageCode,
            platform: 'web',
            countryCode,
          },
          responseType: 'blob',
        })
        if (response?.status === 200 && response.data) {
          const contentDisposition = response.headers?.['content-disposition']
          let filename = 'assessment-report.pdf'
          if (contentDisposition) {
            const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
            if (match?.[1]) filename = match[1].replace(/['"]/g, '')
          }
          const blob = new Blob([response.data], { type: 'application/pdf' })
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.download = filename
          document.body.appendChild(link)
          link.click()
          window.URL.revokeObjectURL(url)
          document.body.removeChild(link)
          toast.success('Report downloaded successfully')
        } else {
          toast.error('Failed to download report')
        }
      } catch (error) {
        console.error('Failed to download report:', error)
        toast.error('Failed to download report. Please try again.')
      } finally {
        setDownloadingPdf(false)
      }
    }

    async function saveAndGeneratePDF(type) {
      await saveTherapyReport()
      const filename = `${
        therapyReportData?.patientName && therapyReportData?.patientName
      } ${getDateOfTherapyReport(therapyReportData)} Prescription.pdf`
      if (type === 'open') {
        generatePDF(targetRef, {
          filename: filename,
          method: 'open',
          page: { margin: Margin.MEDIUM },
        })
      } else {
        generatePDF(targetRef, {
          filename: filename,
          method: 'save',
          page: { margin: Margin.MEDIUM },
        })
      }
    }

    async function saveTriggerFunction() {
      if (triggerSave) {
        await saveTherapyReport()
        if (setTriggerSave) {
          setTriggerSave(false)
        }
        if (afterSaveFunction) {
          afterSaveFunction()
        }
      }
    }

    const showRunDetailsOnPrint = assessmentReportEmrSectionsForPrint.some(
      (s) => s?.id === 'assessment_test_launcher'
    )
    const runDetailsSectionTitle =
      assessmentReportEmrSectionsForPrint.find((s) => s?.id === 'assessment_test_launcher')?.title ??
      'Assessment Run Details'

    function reportPreview({ hiddenInUI = false } = { hiddenInUI: false }) {
      return (
        <PMPParent hiddenInUI={hiddenInUI}>
          <AssessmentReportHeader
            reportData={assessmentReportDataToSave}
            templateData={therapyReportTemplate}
            therapyDate={therapyDate}
          />
          <div style={{ flex: 1, padding: '20px' }}>
            <AssessmentReportBody
              reportData={assessmentReportDataToSave}
              orderedSectionsForPrint={assessmentReportEmrSectionsForPrint}
            />
            {showRunDetailsOnPrint && (
              <AssessmentReportRunDetails
                assessmentRuns={assessmentRunsForPrint}
                sectionTitle={runDetailsSectionTitle}
              />
            )}
          </div>
        </PMPParent>
      )
    }

    // // Debounced version of saveTherapyReport
    // // const debouncedSaveTherapyReport = useCallback(
    // //   _.debounce(() => saveTherapyReport(true), 1500),
    // //   [],
    // // )
    // const debouncedSaveTherapyReport = useMemo(
    //   () => _.debounce(() => saveTherapyReport(true), 1500),
    //   [],
    // )

    // Function to compare if state has changed since last save (including narrative sections)
    const hasStateChanged = () => {
      const currentNarrative = getNarrativeSnapshot()
      const lastNarrative = lastSavedState.current?.narrative
      if (lastNarrative && JSON.stringify(currentNarrative) !== JSON.stringify(lastNarrative)) {
        return true
      }
      return (
        JSON.stringify(lastSavedState.current) !==
        JSON.stringify({
          observationAndAdvice,
          notes,
          selectedComplaints,
          selectedDiagnosis,
          nextVisitInputValue,
          nextVisitInputType,
        })
      )
    }
    useEffect(() => {
      const interval = setInterval(() => {
        if (userEditing && hasStateChanged()) {
          saveTherapyReport(true, false)
        }
      }, 5000)

      return () => clearInterval(interval) // Cleanup on unmount
    }, [
      userEditing,
      observationAndAdvice,
      notes,
      selectedComplaints,
      selectedDiagnosis,
      nextVisitInputValue,
      nextVisitInputType,
      assessmentReportDataToSave?.clinicalInterpretation,
      assessmentReportDataToSave?.functionalImpact,
      assessmentReportDataToSave?.clinicalObservations,
      assessmentReportDataToSave?.provisionalClinicalImpression,
      assessmentReportDataToSave?.recommendations,
      assessmentReportDataToSave?.freeFormRichText,
    ])

    useEffect(() => {
      setup()
    }, [eventId, patientId, auxiliaryDataFromProps])

    useEffect(() => {
      saveTriggerFunction()
    }, [triggerSave])

    useEffect(() => {
      setLauncherActionDrawerOpen(false)
      setLauncherActionAssessmentId(null)
      setLauncherActionClinicalAssessmentTitle(null)
      setLauncherAction(null)
      setLauncherActionCompletedRunId(null)
    }, [eventId])

    // Fetch matching assessment tests for this booked event (event API with includeAssessmentTests=true).
    // Restrict to event's configured clinical assessments when bookedEvent has clinicalAssessmentIds.
    useEffect(() => {
      if (!eventId) {
        setEventAssessmentsForDrawer([])
        return
      }
      let cancelled = false
      const run = async () => {
        try {
          const { eventAssessments } = await getEventAssessments({ bookedEventId: eventId }, false, false)
          if (cancelled) return
          const bookedEvent = bookedEventInfo
          const configuredClinicalIds = Array.isArray(bookedEvent?.clinicalAssessmentIds) && bookedEvent.clinicalAssessmentIds.length > 0
            ? new Set(bookedEvent.clinicalAssessmentIds.map((id) => String(id)))
            : null
          const rawList = eventAssessments ?? []
          const filtered = configuredClinicalIds
            ? rawList.filter((group) => {
                const clinicalId = group?.clinicalAssessment?.id ?? group?.psychAssessment?.id
                return clinicalId && configuredClinicalIds.has(String(clinicalId))
              })
            : rawList
          setEventAssessmentsForDrawer(filtered)
        } catch (e) {
          if (!cancelled) setEventAssessmentsForDrawer([])
        }
      }
      run()
      return () => { cancelled = true }
    }, [
      eventId,
      JSON.stringify(bookedEventInfo?.clinicalAssessmentIds ?? []),
    ])

    // Fetch completed assessment runs for report print (when Assessment Test Launcher is enabled)
    useEffect(() => {
      if (!eventId) {
        setAssessmentRunsForPrint([])
        return
      }
      let cancelled = false
      const run = async () => {
        try {
          const { assessmentRuns } = await getAssessmentRuns({ bookedEventId: eventId, includeDrafts: false })
          if (!cancelled) setAssessmentRunsForPrint(Array.isArray(assessmentRuns) ? assessmentRuns : [])
        } catch {
          if (!cancelled) setAssessmentRunsForPrint([])
        }
      }
      run()
      return () => { cancelled = true }
    }, [eventId])

    // Fetch EMR sections for assessment-report (ordering + print/editor toggles), same API as Settings
    useEffect(() => {
      let cancelled = false
      const run = async () => {
        try {
          const apiUrl = '/api/secure/practitioner/clinical/emr-settings/emr-sections?includeAll=true'
          const resp = await getMPDataWithoutParams(apiUrl)
          if (cancelled) return
          const dataMap = resp?.data?.data ?? resp?.data ?? {}
          const reportData = dataMap['assessment-report']
          const rawSections = reportData?.emrSections ?? []
          const withOrder = rawSections
            .filter((s) => Boolean(s?.selected) && s?.enabled !== false)
            .sort((a, b) => Number(a?.order ?? 0) - Number(b?.order ?? 0))
          // Editor: selected, enabled, and not print-only (like prescription page)
          setAssessmentReportEmrSections(withOrder.filter((s) => s.printOnly !== true))
          // Print/preview: selected, enabled, printable (printable !== false), not editor-only
          setAssessmentReportEmrSectionsForPrint(
            withOrder.filter((s) => s.printable !== false && s.editorOnly !== true),
          )
        } catch (e) {
          if (!cancelled) {
            setAssessmentReportEmrSections([])
            setAssessmentReportEmrSectionsForPrint([])
          }
        }
      }
      run()
      return () => { cancelled = true }
    }, [])

    // SIMPLIFIED DESIGN: Child component does NOT manage header state
    // Parent component (MentorClinicSomeChamberAutoJoiningPage) is the single source of truth
    // This component just renders its content - header is managed by parent based on selectedOptionPatientMenu
    // Removed all header-setting logic to prevent race conditions

    const bookedEventTitle = propBookedEventInfo?.title || 'Assessment Report'

    if (mentorProfileDataLoading) {
      return <LoadingSpinner />
    } else {
      return (
        <PrescriptionTabFinalParent style={{ width: '100%' }}>
          {/* Fixed floating Save buttons on the right (like Prescription Making page) */}
          <div
            className="hide-on-print-only"
            style={{
              position: 'fixed',
              top: '125px',
              right: '15px',
              zIndex: 1001,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              gap: '8px',
              boxShadow: 'rgba(50, 50, 93, 0.25) 0px 13px 27px -5px, rgba(0, 0, 0, 0.3) 0px 8px 16px -8px',
              background: 'white',
              padding: '10px 12px',
              borderRadius: '12px',
              border: '1px solid rgba(99, 102, 241, 0.15)',
            }}
          >
            {renderInfoText()}
            <PMPButtonForConsultation
              style={{ pointerEvents: 'all', width: 'min-content' }}
              whitebg
              onClick={() => saveAndPrint()}
            >
              <Tooltip title="Save and Print" placement="left">
                <PrintOutlined />
              </Tooltip>
            </PMPButtonForConsultation>
            <PMPButtonForConsultation
              style={{ pointerEvents: 'all', width: 'min-content' }}
              disabled={prescriptionSaveLoading}
              onClick={() => saveTherapyReport()}
            >
              <Tooltip title="Save" placement="left">
                <Save />
              </Tooltip>
              {prescriptionSaveLoading && (
                <Spinner animation="border" variant="light" size="sm" style={{ marginLeft: '4px' }} />
              )}
            </PMPButtonForConsultation>
            <PMPButtonForConsultation
              style={{ pointerEvents: 'all', width: 'min-content' }}
              whitebg
              disabled={downloadingPdf}
              onClick={() => downloadAssessmentReportPdf()}
            >
              <Tooltip title="Download PDF" placement="left">
                <Download />
              </Tooltip>
              {downloadingPdf && (
                <Spinner animation="border" variant="light" size="sm" style={{ marginLeft: '4px' }} />
              )}
            </PMPButtonForConsultation>
            <PMPButtonForConsultation
              style={{ pointerEvents: 'all', width: 'min-content' }}
              whitebg
              onClick={() => emrSectionsEditorHandleOpen()}
            >
              <Tooltip title="Assessment Report Settings" placement="left">
                <Settings />
              </Tooltip>
            </PMPButtonForConsultation>
          </div>

          {/* EMR Sections Editor (same as prescription page Settings shortcut); fetches .../emr-sections?includeAll=true, shows only assessment-report */}
          <EmrSectionsEditorDialog
            open={emrSectionsEditorOpen}
            handleClose={emrSectionsEditorHandleClose}
            refreshEmrSections={refreshEmrSections}
            mentorProfileData={propMentorProfileData}
            emrReportType="assessment-report"
          />

          <div className="hide-on-print-only" style={{ margin: '0 auto', width: '95%', paddingTop: '48px', boxSizing: 'border-box' }}>
            {/* Booked event title – top center tag */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginTop: '24px',
                marginBottom: '20px',
              }}
            >
              <Chip
                label={bookedEventTitle}
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '14px', md: '16px' },
                  color: '#fff',
                  background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.95) 0%, rgba(79, 70, 229, 0.9) 100%)',
                  border: '1px solid rgba(255, 255, 255, 0.25)',
                  boxShadow: '0 2px 8px rgba(99, 102, 241, 0.3)',
                  px: 2.5,
                  py: 1.25,
                  '& .MuiChip-label': { px: 0 },
                }}
              />
            </Box>

            <PMPSectionHeading>Patient Details</PMPSectionHeading>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <div style={{ display: 'flex', gap: '20px' }}>
                <PMPSectionInputLabel>Patient Name: </PMPSectionInputLabel>
                <PMPSectionInputLabel>
                  {therapyReportData?.patientName} ({therapyReportData?.patientNumber})
                </PMPSectionInputLabel>
              </div>
              <div style={{ display: 'flex', gap: '20px' }}>
                <PMPSectionInputLabel>Age/Gender: </PMPSectionInputLabel>
                <PMPSectionInputLabel>
                  {getAgeFromDobWithTime(therapyReportData?.patientDateOfBirth)}/
                  {
                    GENDER_LIST_ONBOARDING?.find(
                      (item) => item.value === therapyReportData?.patientGender,
                    )?.label
                  }
                </PMPSectionInputLabel>
              </div>
              <div style={{ display: 'flex', gap: '20px' }}>
                <PMPSectionInputLabel>Date: </PMPSectionInputLabel>
                <PMPSectionInputLabel>
                  {dayjs.utc(therapyDate).local().format('DD/MM/YYYY')}
                </PMPSectionInputLabel>
              </div>
            </div>
            <PsychAssessmentViewerAndSignaller
              psychAssessmentId={propBookedEventInfo?.psychAssessmentIds?.[0]}
              chamberRoomId={chamberRoomId}
              assessmentData={assessmentReportDataToSave}
              setAssessmentData={setAssessmentReportDataToSave}
              // propBookedEventInfo={propBookedEventInfo}
            />

            {/* Single flat list in EMR section order: all sections from EMR (narrative, private notes, assessment test launcher, signature) */}
            {(assessmentReportEmrSections.length > 0 ? assessmentReportEmrSections : ASSESSMENT_REPORT_NARRATIVE_SECTIONS.map((s) => ({ id: s.id, title: s.title }))).map((emrSection) => {
              const sectionId = emrSection.id
              const isPrivateNotes = sectionId === 'private_notes' || sectionId === 'private-notes'
              const isAssessmentTestLauncher = sectionId === 'assessment_test_launcher'
              const isSignature = sectionId === 'signature'
              const section = ASSESSMENT_REPORT_NARRATIVE_SECTIONS.find((n) => n.id === sectionId)

              if (isPrivateNotes) {
                return (
                  <Box
                    key={sectionId}
                    sx={{
                      marginBottom: '20px',
                      marginTop: '24px',
                      border: '1px solid rgba(99, 102, 241, 0.2)',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      backgroundColor: '#fff',
                    }}
                  >
                    <Box
                      sx={{
                        padding: '12px 16px',
                        background: 'rgba(99, 102, 241, 0.04)',
                        borderBottom: '1px solid rgba(99, 102, 241, 0.1)',
                        fontWeight: 600,
                        fontSize: '15px',
                        color: '#1e293b',
                      }}
                    >
                      {emrSection.title ?? 'Private Notes'}
                    </Box>
                    <Box sx={{ padding: '16px' }}>
                      <PatientNotesSectionForReports
                        ref={notesRef}
                        patientName={therapyReportData?.patientName}
                        thisBookedEventId={eventId}
                        patientId={patientId}
                      />
                    </Box>
                  </Box>
                )
              }

              if (isAssessmentTestLauncher) {
                return (
                  <Box
                    key={sectionId}
                    sx={{
                      marginBottom: '20px',
                      marginTop: '24px',
                      border: '1px solid rgba(99, 102, 241, 0.2)',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      backgroundColor: '#fff',
                    }}
                  >
                    <Box
                      sx={{
                        padding: '12px 16px',
                        background: 'rgba(99, 102, 241, 0.04)',
                        borderBottom: '1px solid rgba(99, 102, 241, 0.1)',
                        fontWeight: 600,
                        fontSize: '15px',
                        color: '#1e293b',
                      }}
                    >
                      {emrSection.title ?? 'Assessment tests'}
                    </Box>
                    <Box sx={{ padding: '16px' }}>
                      {eventId ? (
                        <PractitionerAssessmentDrawer
                          key={`assessment-inline-${eventId}`}
                          contentOnly
                          renderListOnly
                          open={!!eventId}
                          onListActionClick={(id, title, action, completedRunId, clinicalAssessmentTitle) => {
                            setLauncherActionAssessmentId(id)
                            setLauncherActionClinicalAssessmentTitle(clinicalAssessmentTitle ?? null)
                            setLauncherAction(action)
                            setLauncherActionCompletedRunId(completedRunId ?? null)
                            setLauncherActionDrawerOpen(true)
                          }}
                          bookedEventId={eventId}
                          bookedEventInfo={assessmentDrawerBookedEventInfo}
                          assessmentReport={assessmentReportDataToSave}
                          patientId={patientId}
                          mentorId={propMentorProfileData?.id}
                          isAssessmentEventPage={true}
                          initialEventAssessments={eventAssessmentsForDrawer.length > 0 ? eventAssessmentsForDrawer : undefined}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No session linked. Assessment tests are available when editing a booked session.
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )
              }

              if (isSignature) {
                return (
                  <Box
                    key={sectionId}
                    sx={{
                      marginBottom: '20px',
                      marginTop: '24px',
                      border: '1px solid rgba(99, 102, 241, 0.2)',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      backgroundColor: '#fff',
                    }}
                  >
                    <Box
                      sx={{
                        padding: '12px 16px',
                        background: 'rgba(99, 102, 241, 0.04)',
                        borderBottom: '1px solid rgba(99, 102, 241, 0.1)',
                        fontWeight: 600,
                        fontSize: '15px',
                        color: '#1e293b',
                      }}
                    >
                      {emrSection.title ?? 'Signature'}
                    </Box>
                    <Box sx={{ padding: '16px' }}>
                      <Typography variant="body2" color="text.secondary">
                        Signature is included in the report header/footer when printing.
                      </Typography>
                    </Box>
                  </Box>
                )
              }

              if (!section) return null
              return (
                <Box
                  key={section.id}
                  sx={{
                    marginBottom: '20px',
                    marginTop: '24px',
                    border: '1px solid rgba(99, 102, 241, 0.2)',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    backgroundColor: '#fff',
                  }}
                >
                  <Box
                    sx={{
                      padding: '12px 16px',
                      background: 'rgba(99, 102, 241, 0.04)',
                      borderBottom: '1px solid rgba(99, 102, 241, 0.1)',
                      fontWeight: 600,
                      fontSize: '15px',
                      color: '#1e293b',
                    }}
                  >
                    {emrSection.title ?? section.title}
                  </Box>
                  <Box sx={{ padding: '16px' }}>
                    <TipTapEditor
                      key={`${section.id}-${therapyReportId ?? assessmentReportDataToSave?.id ?? assessmentReportDataToSave?.assessmentReportId ?? 'draft'}`}
                      contentMinHeight={72}
                      value={assessmentReportDataToSave?.[section.field] ?? ''}
                      onChange={(html) => {
                        setUserEditing(true)
                        setAssessmentReportDataToSave((prev) => ({
                          ...prev,
                          [section.field]: html ?? '',
                        }))
                      }}
                      placeholder={`Write ${(emrSection.title ?? section.title).toLowerCase()}...`}
                    />
                  </Box>
                </Box>
              )
            })}

            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '10px' }}
            >
              {/* <PrescriptionNextVisitComponent
                nextVisitInputValue={nextVisitInputValue}
                setNextVisitInputValue={(val) => setUserEditingState(setNextVisitInputValue, val)}
                nextVisitInputType={nextVisitInputType}
                setNextVisitInputType={(val) => setUserEditingState(setNextVisitInputType, val)}
              /> */}

              <Dialog
                open={showConfirmation}
                onClose={() => {
                  setNextVisitOmissionReason('')
                  setShowConfirmation(false)
                }}
              >
                <TherapyReportConfirmation
                  closeModal={() => {
                    setNextVisitOmissionReason('')
                    setShowConfirmation(false)
                  }}
                  isSaving={prescriptionSaveLoading}
                  reportReason={nextVisitOmissionReason}
                  setReportReason={setNextVisitOmissionReason}
                  submitForm={saveReason}
                />
              </Dialog>
            </div>

            <hr />

            <PMPSectionHeading>View Report</PMPSectionHeading>
            {reportPreview()}
          </div>
          {reportPreview({ hiddenInUI: true })}
          <div
            className={!isPrintingPastHistoryReports ? 'hide-on-print' : ''}
            style={{ margin: '0 auto', width: '95%' }}
          >
            <hr style={{ marginTop: '160px' }} className="hide-on-print-only" />

            <PMPSectionHeading
              // ref={historySectionRef}
              className="hide-on-print-only"
              style={{ marginTop: '160px' }}
            >
              Patient History
            </PMPSectionHeading>
            {propBookedEventInfo?.patientId && (
              <LSPastVisitReportsComponent
                patientId={propBookedEventInfo?.patientId}
                referenceBookedEventId={eventId}
                // screeningSections={screeningSections}
                setIsPrintingPastHistoryReports={setIsPrintingPastHistoryReports}
                mentorPrivateNotes={auxiliaryDataFromProps?.mentorPrivateNotes}
              />
            )}
          </div>

          {/* Launcher action drawer: opens when user clicks Start/Resume/View Result etc. from the Assessment Test Launcher list (no parentBookedEventId; this event is the main report event) */}
          {eventId && (
            <Drawer
              anchor="right"
              open={launcherActionDrawerOpen}
              onClose={() => {
                setLauncherActionDrawerOpen(false)
                setLauncherActionAssessmentId(null)
                setLauncherActionClinicalAssessmentTitle(null)
                setLauncherAction(null)
                setLauncherActionCompletedRunId(null)
              }}
              PaperProps={{
                sx: { width: { xs: '100%', sm: 640 }, maxWidth: '100%' },
              }}
            >
              <PractitionerAssessmentDrawer
                key={`assessment-launcher-${eventId}`}
                contentOnly
                open={launcherActionDrawerOpen}
                drawerHeading={launcherActionClinicalAssessmentTitle ?? undefined}
                onClose={() => {
                  setLauncherActionDrawerOpen(false)
                  setLauncherActionAssessmentId(null)
                  setLauncherActionClinicalAssessmentTitle(null)
                  setLauncherAction(null)
                  setLauncherActionCompletedRunId(null)
                }}
                bookedEventId={eventId}
                bookedEventInfo={assessmentDrawerBookedEventInfo}
                onRunSubmitted={() => getProcessAndSetEventData?.({ hideLoader: true })}
                assessmentReport={assessmentReportDataToSave}
                patientId={patientId}
                mentorId={propMentorProfileData?.id}
                onAssessmentReportSaved={() => getProcessAndSetEventData?.({ hideLoader: true })}
                isAssessmentEventPage={true}
                initialEventAssessments={eventAssessmentsForDrawer.length > 0 ? eventAssessmentsForDrawer : undefined}
                openWithAssessmentId={launcherActionAssessmentId ?? undefined}
                openWithAction={launcherAction ?? undefined}
                openWithCompletedRunId={launcherActionCompletedRunId ?? undefined}
                onOpenWithHandled={() => {
                  setLauncherActionAssessmentId(null)
                  setLauncherActionClinicalAssessmentTitle(null)
                  setLauncherAction(null)
                  setLauncherActionCompletedRunId(null)
                }}
              />
            </Drawer>
          )}
        </PrescriptionTabFinalParent>
      )
    }
  },
)

export default AssessmentReportMakingPage
